import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Building2, Send, Pencil, Users, Eye } from 'lucide-react';
import api from '../lib/axios';
import TelegramPanel from '../components/TelegramPanel';
import TenantMembersPanel from '../components/TenantMembersPanel';
import TenantNavPanel from '../components/TenantNavPanel';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  contactEmail: string | null;
  plan: string;
  active: boolean;
  schemaName: string;
}

type TenantForm = {
  slug: string;
  name: string;
  contactEmail: string;
  plan: string;
  active: boolean;
};

const EMPTY: TenantForm = { slug: '', name: '', contactEmail: '', plan: 'FREE', active: true };
const PLANS = ['FREE', 'STANDARD', 'PRO'];
const SLUG_PATTERN = /^[a-z0-9-]{2,50}$/;

/** Derives a slug candidate from a free-text name. */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

export default function Tenants() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [form, setForm] = useState<TenantForm>({ ...EMPTY });
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [telegramSlug, setTelegramSlug] = useState<string | null>(null);
  const [membersSlug, setMembersSlug] = useState<string | null>(null);
  const [navSlug, setNavSlug] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<Tenant[]>('/tenants').then((r) => r.data),
  });

  const closeForm = () => {
    qc.invalidateQueries({ queryKey: ['tenants'] });
    setShowForm(false);
    setEditingSlug(null);
    setForm({ ...EMPTY });
    setSlugTouched(false);
  };

  const create = useMutation({
    mutationFn: () =>
      api
        .post<Tenant>('/tenants', {
          slug: form.slug.trim(),
          name: form.name.trim(),
          contactEmail: form.contactEmail.trim() || null,
        })
        .then((r) => r.data),
    onSuccess: closeForm,
    onError: (e: any) =>
      setError(e?.response?.data?.message ?? t('tenants.createError')),
  });

  const update = useMutation({
    mutationFn: () =>
      api
        .put<Tenant>(`/tenants/${editingSlug}`, {
          name: form.name.trim(),
          contactEmail: form.contactEmail.trim() || null,
          plan: form.plan,
          active: form.active,
        })
        .then((r) => r.data),
    onSuccess: closeForm,
    onError: (e: any) =>
      setError(e?.response?.data?.message ?? t('tenants.updateError')),
  });

  const onName = (value: string) =>
    setForm((f) => ({
      ...f,
      name: value,
      slug: slugTouched ? f.slug : slugify(value),
    }));

  const submit = () => {
    setError(null);
    if (!form.name.trim()) {
      setError(t('tenants.missingName'));
      return;
    }
    if (editingSlug) {
      update.mutate();
      return;
    }
    if (!SLUG_PATTERN.test(form.slug.trim())) {
      setError(t('tenants.invalidSlug'));
      return;
    }
    create.mutate();
  };

  const openCreate = () => {
    setError(null);
    setEditingSlug(null);
    setForm({ ...EMPTY });
    setSlugTouched(false);
    setShowForm(true);
  };

  const startEdit = (tenant: Tenant) => {
    setError(null);
    setEditingSlug(tenant.slug);
    setForm({
      slug: tenant.slug,
      name: tenant.name,
      contactEmail: tenant.contactEmail ?? '',
      plan: tenant.plan,
      active: tenant.active,
    });
    setShowForm(true);
  };

  const tenants = data ?? [];

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t('nav.tenants')}</p>
          <h1 className="page-title mt-1">{t('tenants.title')}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t('tenants.subtitle')}</p>
        </div>
        <button
          onClick={() => (showForm ? closeForm() : openCreate())}
          className={showForm ? 'btn btn-secondary' : 'btn btn-primary'}
        >
          {showForm ? (
            t('common.close')
          ) : (
            <>
              <Plus size={16} /> {t('tenants.new')}
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="reveal card mb-6 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="field-label">{t('tenants.name')}</label>
              <input
                value={form.name}
                onChange={(e) => onName(e.target.value)}
                className="input"
                placeholder={t('tenants.namePlaceholder')}
              />
            </div>
            <div>
              <label className="field-label">{t('tenants.slug')}</label>
              <input
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((f) => ({ ...f, slug: e.target.value }));
                }}
                className="input"
                placeholder="mon-association"
                spellCheck={false}
                disabled={!!editingSlug}
              />
              <p className="mt-1 text-xs text-ink-muted">
                {editingSlug ? t('tenants.slugLocked') : t('tenants.slugHint')}
              </p>
            </div>
            <div>
              <label className="field-label">{t('tenants.contactEmail')}</label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                className="input"
                placeholder="contact@association.fr"
              />
            </div>
            {editingSlug && (
              <>
                <div>
                  <label className="field-label">{t('tenants.plan')}</label>
                  <select
                    value={form.plan}
                    onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
                    className="input"
                  >
                    {PLANS.map((plan) => (
                      <option key={plan} value={plan}>
                        {plan}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    />
                    {t('tenants.active')}
                  </label>
                </div>
              </>
            )}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1" />
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button
              onClick={submit}
              disabled={create.isPending || update.isPending}
              className="btn btn-primary"
            >
              {editingSlug
                ? update.isPending
                  ? t('tenants.saving')
                  : t('tenants.save')
                : create.isPending
                  ? t('tenants.creating')
                  : t('tenants.create')}
            </button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}
      {!isLoading && tenants.length === 0 && (
        <div className="card p-8 text-center text-ink-muted">{t('tenants.empty')}</div>
      )}

      {tenants.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('tenants.name')}</th>
                <th>{t('tenants.slug')}</th>
                <th>{t('tenants.plan')}</th>
                <th>{t('tenants.contactEmail')}</th>
                <th>{t('field.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td data-label={t('tenants.name')}>
                    <span className="flex items-center gap-2 font-medium text-ink">
                      <Building2 size={16} className="text-ink-muted" />
                      {tenant.name}
                    </span>
                  </td>
                  <td data-label={t('tenants.slug')} className="text-ink-soft">{tenant.slug}</td>
                  <td data-label={t('tenants.plan')}>
                    <span className="pill pill-neutral">{tenant.plan}</span>
                  </td>
                  <td data-label={t('tenants.contactEmail')} className="text-ink-soft">{tenant.contactEmail || t('common.none')}</td>
                  <td data-label={t('field.status')}>
                    <span className={tenant.active ? 'pill pill-posted' : 'pill pill-neutral'}>
                      {tenant.active ? t('tenants.active') : t('tenants.inactive')}
                    </span>
                  </td>
                  <td className="cell-actions text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => startEdit(tenant)}
                        className="btn btn-ghost"
                        title={t('tenants.edit')}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => setMembersSlug(tenant.slug)}
                        className="btn btn-ghost"
                        title={t('members.title')}
                      >
                        <Users size={16} /> {t('members.title')}
                      </button>
                      <button
                        onClick={() => setNavSlug(tenant.slug)}
                        className="btn btn-ghost"
                        title={t('settings.navVisibility')}
                      >
                        <Eye size={16} /> {t('settings.navVisibility')}
                      </button>
                      <button
                        onClick={() => setTelegramSlug(tenant.slug)}
                        className="btn btn-ghost"
                        title={t('telegram.title')}
                      >
                        <Send size={16} /> {t('telegram.title')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {telegramSlug && (
        <TelegramPanel slug={telegramSlug} onClose={() => setTelegramSlug(null)} />
      )}
      {membersSlug && (
        <TenantMembersPanel slug={membersSlug} onClose={() => setMembersSlug(null)} />
      )}
      {navSlug && (
        <TenantNavPanel slug={navSlug} onClose={() => setNavSlug(null)} />
      )}
    </div>
  );
}
